import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  UserGroupIcon,
  CalculatorIcon,
  TagIcon,
  DocumentTextIcon,
  BanknotesIcon,
  RocketLaunchIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const PASOS_TOUR = [
  {
    id: 'bienvenida',
    titulo: 'Bienvenido a Wolf Eye Sistema de Gestión para Ópticas',
    subtitulo: 'Una plataforma integral en la nube diseñada exclusivamente para el flujo de trabajo de ópticas independientes y cadenas.',
    icono: SparklesIcon,
    colorAccent: 'from-[#0052b3] via-[#006be6] to-[#0284c7]',
    destacados: [
      { titulo: 'Flujo 100% Óptico', desc: 'Pensado desde el mostrador: desde la toma de receta hasta la entrega del anteojo.' },
      { titulo: 'Todo en un solo lugar', desc: 'Pacientes, cristales, armazones, facturación fiscal AFIP, caja y tienda online.' },
      { titulo: 'Modo Demostración Activo', desc: 'Puedes navegar y explorar todas las vistas con datos de prueba reales.' }
    ],
    tip: 'Deslizá hacia los lados para recorrer cada módulo.'
  },
  {
    id: 'pacientes',
    titulo: 'Gestión Integral de Pacientes y Graduaciones',
    subtitulo: 'Historial clínico completo y seguimiento detallado del estado de cada anteojo.',
    icono: UserGroupIcon,
    colorAccent: 'from-[#004a99] via-[#0062cc] to-[#1d4ed8]',
    destacados: [
      { titulo: 'Ficha Clínica Completa', desc: 'Graduación Lejos, Cerca, Intermedia, Multifocal, prismas, adición y distancia pupilar.' },
      { titulo: 'Replicado Inteligente de Recetas', desc: 'Copia la graduación previa del paciente con un solo clic para evitar errores de tipeo.' },
      { titulo: 'Trazabilidad de Taller', desc: 'Estados visuales claros: Tomados → En Laboratorio → Para Retirar → Entregados.' }
    ],
    tip: 'Incluye impresión de sobres de taller estandarizados para mandar a laboratorio.'
  },
  {
    id: 'presupuestos',
    titulo: 'Cotizador Inteligente y Presupuestos HD',
    subtitulo: 'Cotiza cristales y lentes de contacto en segundos y envía tarjetas visuales premium por WhatsApp.',
    icono: CalculatorIcon,
    colorAccent: 'from-[#005fb8] via-[#0284c7] to-[#2563eb]',
    destacados: [
      { titulo: 'Multigraduación en tiempo real', desc: 'Compara al instante Monofocales, Ocupacionales, Bifocales, Multifocales y Lentes de Contacto.' },
      { titulo: 'Tarjetas Gráficas de Alta Definición', desc: 'Genera tarjetas de presupuestos listas para compartir por WhatsApp con el cliente.' },
      { titulo: 'Simulador de Financiación', desc: 'Calcula cuotas sin interés y precio en efectivo con el descuento configurado por óptica.' }
    ],
    tip: 'Mejora la tasa de cierre en mostrador mostrando al cliente opciones claras y visuales.'
  },
  {
    id: 'precios',
    titulo: 'Lista de Precios y Políticas Comerciales',
    subtitulo: 'Catálogo de cristales y armazones con búsqueda en milisegundos y reglas por marca.',
    icono: TagIcon,
    colorAccent: 'from-[#0066cc] via-[#0284c7] to-[#0d9488]',
    destacados: [
      { titulo: 'Buscador Instantáneo', desc: 'Filtra cristales por tratamientos: Antirreflejo, Blue Block, Fotocromático y laboratorio.' },
      { titulo: 'Reglas Comerciales por Proveedor', desc: 'Configura descuentos y cuotas sin interés máximas por proveedor o marcas.' },
      { titulo: 'Cálculo de Precio Efectivo Automático', desc: 'Aplica el porcentaje de descuento y redondeo a miles de forma transparente.' }
    ],
    tip: 'Permite importar facturas de proveedores oficiales en PDF para cargar inventario masivo.'
  },
  {
    id: 'facturacion',
    titulo: 'Facturación Electrónica AFIP Integrada',
    subtitulo: 'Emisión fiscal directa de Facturas B y Notas de Crédito autorizadas por CAE en un clic.',
    icono: DocumentTextIcon,
    colorAccent: 'from-[#005fb8] via-[#0369a1] to-[#0284c7]',
    destacados: [
      { titulo: 'Conexión Directa con AFIP', desc: 'Sin intermediarios ni planillas externas. Obtención de CAE oficial inmediata.' },
      { titulo: 'Notas de Crédito Automáticas', desc: 'Anulación o ajuste fiscal con un clic referenciando la factura de origen.' },
      { titulo: 'Asistente de Certificados Digitales', desc: 'Guía paso a paso para asociar el Punto de Venta y subir el certificado de AFIP.' }
    ],
    tip: 'Cumple con todas las regulaciones tributarias argentinas de manera transparente.'
  },
  {
    id: 'finanzas',
    titulo: 'Finanzas, Caja Diaria y Balances',
    subtitulo: 'Control milimétrico del dinero que ingresa y egresa de tu negocio.',
    icono: BanknotesIcon,
    colorAccent: 'from-[#0052b3] via-[#1e40af] to-[#312e81]',
    destacados: [
      { titulo: 'Arqueo de Caja Diario', desc: 'Desglose exacto de cobros en Efectivo, Tarjetas de Débito/Crédito y Transferencias.' },
      { titulo: 'Control de Saldos Pendientes', desc: 'Seguimiento de señas y cobros pendientes contra la entrega del anteojo.' },
      { titulo: 'Reportes y Balances Mensuales', desc: 'Métricas de rentabilidad y registro de egresos comerciales en tiempo real.' }
    ],
    tip: 'Evita pérdidas por anteojos listos que los clientes demoran en retirar y abonar el saldo.'
  },
  {
    id: 'ecommerce',
    titulo: 'Tu Propia Tienda Online (E-commerce Integrado)',
    subtitulo: 'Vende armazones y lentes de sol las 24 horas del día con tu propio catálogo web.',
    icono: GlobeAltIcon,
    colorAccent: 'from-[#0052b3] via-[#2563eb] to-[#6d28d9]',
    destacados: [
      { titulo: 'Catálogo Web con Fotos de Drive', desc: 'Publica productos con solo pegar el enlace compartido de Google Drive.' },
      { titulo: 'Cobro en Línea y Transferencias', desc: 'Pasarela conectada con Mercado Pago o transferencia bancaria con descuentos automáticos.' },
      { titulo: 'Gestión de Envíos y Retiros', desc: 'Opciones de retiro en óptica o envíos a domicilio con control de stock automático.' }
    ],
    tip: 'Permite a tus clientes explorar tus armazones desde su teléfono antes de ir al local.'
  },
  {
    id: 'activar',
    titulo: '¡Comienza con tu Óptica hoy mismo!',
    subtitulo: 'Te regalamos 3 meses de acceso completo y privado para que cargues a tus pacientes y productos.',
    icono: RocketLaunchIcon,
    colorAccent: 'from-[#0060c4] via-[#0284c7] to-[#059669]',
    destacados: [
      { titulo: '3 Meses 100% Gratis', desc: 'Sin costo de activación ni tarjeta de crédito requerida.' },
      { titulo: 'Espacio Privado e Independiente', desc: 'Tus pacientes y datos comerciales protegidos bajo tu propia óptica.' },
      { titulo: 'Activación Instantánea', desc: 'Solo ingresas el nombre de tu óptica y ya estás listo para operar.' }
    ],
    tip: 'Tocá "Activar mi Óptica" abajo para crear tu cuenta en 30 segundos.'
  }
];

const SWIPE_THRESHOLD = 45;

const ModalDemoTour = ({ isOpen, onClose, onAbrirRegistro }) => {
  const [pasoActual, setPasoActual] = useState(0);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const touchStartRef = useRef({ x: 0, y: 0, lockedAxis: null });

  const esUltimoPaso = pasoActual === PASOS_TOUR.length - 1;

  const irSiguiente = useCallback(() => {
    if (esUltimoPaso) {
      if (onAbrirRegistro) onAbrirRegistro();
      onClose();
    } else {
      setPasoActual((prev) => Math.min(PASOS_TOUR.length - 1, prev + 1));
    }
  }, [esUltimoPaso, onAbrirRegistro, onClose]);

  const irAnterior = useCallback(() => {
    setPasoActual((prev) => Math.max(0, prev - 1));
  }, []);

  // Navegación con teclado (flechas izquierda/derecha y Escape)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        if (pasoActual < PASOS_TOUR.length - 1) {
          setPasoActual((prev) => prev + 1);
        }
      } else if (e.key === 'ArrowLeft') {
        if (pasoActual > 0) {
          setPasoActual((prev) => prev - 1);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, pasoActual, onClose]);

  if (!isOpen) return null;

  const paso = PASOS_TOUR[pasoActual];
  const IconoPaso = paso.icono;

  // Gestos táctiles (Swipe izquierda / derecha entre módulos)
  const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length !== 1) return;
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      lockedAxis: null
    };
    setIsDragging(true);
    setDragOffsetX(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !e.touches || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    if (!touchStartRef.current.lockedAxis) {
      if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
        touchStartRef.current.lockedAxis = Math.abs(deltaX) >= Math.abs(deltaY) ? 'x' : 'y';
      }
    }

    if (touchStartRef.current.lockedAxis === 'x') {
      // Resistencia suave en los extremos
      const atEdge =
        (pasoActual === 0 && deltaX > 0) ||
        (pasoActual === PASOS_TOUR.length - 1 && deltaX < 0);
      const dampedDelta = atEdge ? deltaX * 0.25 : Math.max(-110, Math.min(110, deltaX * 0.65));
      setDragOffsetX(dampedDelta);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (touchStartRef.current.lockedAxis === 'x') {
      if (dragOffsetX <= -SWIPE_THRESHOLD && pasoActual < PASOS_TOUR.length - 1) {
        setPasoActual((prev) => prev + 1);
      } else if (dragOffsetX >= SWIPE_THRESHOLD && pasoActual > 0) {
        setPasoActual((prev) => prev - 1);
      }
    }

    setDragOffsetX(0);
    touchStartRef.current.lockedAxis = null;
  };

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center p-2.5 sm:p-5 overflow-y-auto bg-slate-950/85 backdrop-blur-md animate-fadeIn font-tour">
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        className="relative w-full max-w-2xl my-auto bg-white rounded-3xl shadow-2xl border border-slate-200/80 overflow-hidden flex flex-col max-h-[92vh] select-none sm:select-auto"
      >
        {/* Header limpio y moderno con gradiente */}
        <div className={`relative bg-gradient-to-br ${paso.colorAccent} px-5 pt-6 pb-5 sm:px-8 sm:pt-8 sm:pb-6 text-white transition-colors duration-500 shrink-0`}>
          {/* Botón cerrar */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3.5 right-3.5 sm:top-5 sm:right-5 p-2 text-white/80 hover:text-white bg-black/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
            title="Cerrar tour"
            aria-label="Cerrar tour"
          >
            <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          {/* Encabezado del paso */}
          <div className="flex items-center gap-3.5 sm:gap-4 pr-9">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-lg shrink-0">
              <IconoPaso className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl lg:text-[26px] font-semibold text-white leading-tight tracking-tight">
                {paso.titulo}
              </h2>
            </div>
          </div>

          <p className="text-xs sm:text-sm lg:text-base text-blue-50/95 font-normal mt-3 leading-relaxed">
            {paso.subtitulo}
          </p>
        </div>

        {/* Contenido del paso con feedback visual al deslizar */}
        <div
          style={{
            transform: dragOffsetX ? `translateX(${dragOffsetX}px)` : 'translateX(0px)',
            opacity: dragOffsetX ? Math.max(0.7, 1 - Math.abs(dragOffsetX) / 300) : 1,
            transition: isDragging ? 'none' : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease'
          }}
          className="p-4 sm:p-7 overflow-y-auto space-y-3 sm:space-y-4 flex-1"
        >
          <div className="space-y-2.5 sm:space-y-3">
            {paso.destacados.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 sm:gap-3.5 p-3.5 sm:p-4 rounded-2xl bg-slate-50/90 hover:bg-blue-50/60 border border-slate-200/80 hover:border-blue-200 transition-colors"
              >
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-xl bg-blue-600/10 border border-blue-600/20 flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">
                    {item.titulo}
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-600 mt-0.5 leading-relaxed font-normal">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Tip contextual */}
          {paso.tip && (
            <div className="flex items-start sm:items-center gap-2.5 sm:gap-3 p-3 sm:p-3.5 rounded-2xl bg-amber-50/90 border border-amber-200/90 text-amber-950 text-xs sm:text-sm">
              <CheckBadgeIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5 sm:mt-0" />
              <span className="font-normal leading-snug">{paso.tip}</span>
            </div>
          )}
        </div>

        {/* Footer minimalista y limpio */}
        <div className="bg-slate-50 border-t border-slate-200/90 p-3.5 sm:p-5 sm:px-7 shrink-0 space-y-2.5 sm:space-y-0">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            {/* Botón Anterior + Cerrar en Desktop */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={irAnterior}
                disabled={pasoActual === 0}
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-slate-700 hover:text-slate-950 bg-white sm:bg-transparent hover:bg-slate-200/70 border border-slate-200 sm:border-transparent rounded-xl transition-all disabled:opacity-35 disabled:pointer-events-none cursor-pointer"
              >
                <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Anterior</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                className="text-xs sm:text-sm font-medium text-slate-500 hover:text-slate-800 px-3 py-2 rounded-xl transition-colors hidden md:inline-block cursor-pointer"
              >
                Cerrar y explorar
              </button>
            </div>

            {/* Puntos de progreso discretos */}
            <div className="flex items-center gap-1.5">
              {PASOS_TOUR.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPasoActual(idx)}
                  aria-label={`Ir al paso ${idx + 1}`}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    idx === pasoActual
                      ? 'w-6 bg-blue-600'
                      : idx < pasoActual
                        ? 'w-2 bg-blue-300'
                        : 'w-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>

            {/* Controles derechos */}
            <div className="flex items-center gap-2 sm:gap-2.5">
              {!esUltimoPaso && (
                <button
                  type="button"
                  onClick={() => {
                    if (onAbrirRegistro) onAbrirRegistro();
                    onClose();
                  }}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 text-xs sm:text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all cursor-pointer"
                >
                  <RocketLaunchIcon className="w-4 h-4 text-blue-600" />
                  <span>Activar mi Óptica</span>
                </button>
              )}

              <button
                type="button"
                onClick={irSiguiente}
                className={`inline-flex items-center justify-center gap-1.5 px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white rounded-xl shadow-md transition-all cursor-pointer ${
                  esUltimoPaso
                    ? 'bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 shadow-emerald-600/25'
                    : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/25'
                }`}
              >
                <span>{esUltimoPaso ? '¡Crear mi Óptica Gratis!' : 'Siguiente'}</span>
                {esUltimoPaso ? (
                  <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Barra secundaria en Mobile para Activar Óptica o Explorar sin aplastar los botones de navegación */}
          <div className="flex sm:hidden items-center justify-between gap-2 pt-1 border-t border-slate-200/70">
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-medium text-slate-500 hover:text-slate-800 py-1 px-2 rounded-lg"
            >
              Cerrar y explorar demo
            </button>

            {!esUltimoPaso && (
              <button
                type="button"
                onClick={() => {
                  if (onAbrirRegistro) onAbrirRegistro();
                  onClose();
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl"
              >
                <RocketLaunchIcon className="w-3.5 h-3.5 text-blue-600" />
                <span>Activar 3 Meses Gratis</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ModalDemoTour.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAbrirRegistro: PropTypes.func
};

export default ModalDemoTour;
